import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function NorthTynesideCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="North Tyneside Pothole Claims | A-Road RED Up Sevenfold | Fixtyer"
        description="North Tyneside Council reports A-road RED condition rising from 2% to 14% after switching to AEI surveys, while 722km of estate roads carry a doubled U-road backlog. See what the DfT AMBER rating means for your pothole claim."
        canonical="/councils/north-tyneside"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'North Tyneside', url: '/councils/north-tyneside' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'North Tyneside has a GREEN spend scorecard — can I still claim for pothole damage?',
            answer: 'Yes. The DfT Spend scorecard is GREEN, but your claim turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired under Section 58 — not on aggregate spend. North Tyneside is AMBER overall for condition, its A-road RED share rose from 2% to 14% on published data, and U-road RED more than doubled from 4% to 11% before easing to 10%. GREEN spend does not erase those condition trends.',
          },
          {
            question: 'What does the jump in A-road RED condition mean for my claim?',
            answer: 'North Tyneside\'s own transparency report shows A-road RED condition at 2% in 2020 and 2021, then 13% in 2023 and 14% in 2024 — a sevenfold rise on the council\'s published figures. The council attributes much of the jump to switching from SCANNER to Annual Engineering Inspections (AEI) in 2023. That admission matters: if the council cites improving methodology while your incident predates or sits inside the transition, ask whether pre- and post-2023 condition records are comparable for your specific road.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 722km — 80% of North Tyneside\'s 898km carriageway network. The council\'s published RED share for U-roads rose from 4% in 2020 to 11% in 2023, then 10% in 2024 — roughly 72km of estate highway in RED condition at the latest survey. North Tyneside now assesses the full carriageway network annually using AEI, but pothole claims still turn on whether your specific defect was identified, prioritised and repaired in time.',
          },
          {
            question: 'Are the pothole repair figures reliable?',
            answer: 'Treat them as estimates. North Tyneside\'s own report states its works ordering system "does not differentiate between pothole repairs and other types of reactive bituminous carriageway repairs", so published pothole counts are the council\'s best assessment of works-ordering data, not a precise pothole-only tally. The trend still matters: an estimated 34,739 repairs over five years, averaging roughly 6,948 per year, shows a network that routinely produces reactive defects.',
          },
          {
            question: 'Does the AEI survey switch weaken a Section 58 defence?',
            answer: 'It can cut both ways. North Tyneside says AEI evaluates entire road sections rather than 10-metre SCANNER sub-sections and admits the change "resulted in a significant jump in the red rated roads figures for all categories of road." That is documentary evidence the council knew its published condition picture shifted sharply in 2023. If the council relies on network-level knowledge under Section 58, you can ask whether the survey that flagged your road was SCANNER or AEI, and whether treatment recommendations from AEI were acted on before your incident.',
          },
          {
            question: 'North Tyneside inspects roads twice a year — is that enough?',
            answer: 'Safety inspections at least twice yearly are logged in the Symology system, separate from annual AEI condition surveys. Section 58 is defect-specific: the question is whether your pothole should have been found on a safety inspection, reported by a resident, or flagged by AEI before it damaged your vehicle. Prior reports through the council\'s online roads form or FixMyStreet — combined with photos showing defect age — are often stronger than arguing inspection frequency alone.',
          },
          {
            question: 'Why is Best Practice AMBER when the council reports 88–90% preventative spend?',
            answer: 'The DfT Best Practice scorecard measures more than spend split — treatment intensity, innovation adoption and asset-management maturity all feed in. North Tyneside publishes strong preventative percentages and trials warm-mix and rubberised materials, yet carriageway preservation fell from 7.8km in 2023/24 to 2.33km in 2024/25 while A-road RED condition climbed on published data. AMBER best practice alongside AMBER condition means expect a prepared Section 58 defence, but one that still has to answer for your specific defect.',
          },
        ]}
      />
      <ServiceSchema
        councilName="North Tyneside Council"
        councilSlug="north-tyneside"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/north-tyneside"
        headline="North Tyneside Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">North Tyneside</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            North Tyneside: A-Road RED Condition Rose From 2% to 14%
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            North Tyneside spends well above its DfT capital allocation and classifies{' '}
            <strong className="text-emerald-400">88–90% of highway spend as preventative</strong> — earning a GREEN
            spend scorecard. Yet the overall DfT rating is <strong className="text-amber-400">AMBER</strong>, and
            the council's own transparency data shows <strong className="text-rose-400">A-road RED condition up
            sevenfold</strong> since 2020, U-road RED more than doubled to 11%, and an estimated{' '}
            <strong className="text-rose-400">34,739 pothole repairs in five years</strong> across a borough where
            722km of estate roads dominate the network.
          </p>

          {/* Key stat callout */}
          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">14%</div>
            <div className="text-white font-medium mt-2">A-roads in RED condition (2024)</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from 2% in 2020 and 2021 on North Tyneside's published figures — with 29% amber and
              57% green on 104km of A-roads. The council switched to Annual Engineering Inspections in 2023
              and admits the change produced a "significant jump" in red-rated roads.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of published condition data from North Tyneside's own transparency report — a sharp
            deterioration on A-roads after the 2023 survey switch
          </p>

          {/* A-roads */}
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-rose-900 mb-4">A-roads (104km — 11.6% of network): RED share up sevenfold</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-rose-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-rose-100 border-b border-rose-200">
                    <th className="px-4 py-2 text-left font-semibold text-rose-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">2%</td>
                    <td className="px-4 py-2 text-center">16%</td>
                    <td className="px-4 py-2 text-center">81%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">2%</td>
                    <td className="px-4 py-2 text-center">17%</td>
                    <td className="px-4 py-2 text-center">81%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic">N/A</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic">N/A</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">13%</td>
                    <td className="px-4 py-2 text-center">28%</td>
                    <td className="px-4 py-2 text-center">59%</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">14%</td>
                    <td className="px-4 py-2 text-center font-bold">29%</td>
                    <td className="px-4 py-2 text-center font-bold">57%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-rose-800 font-medium">
              On the council's published figures, 43% of North Tyneside's A-road network — roughly 45km — is now
              in amber or red condition. A-roads are a minority of the borough's highways, but they carry the
              heaviest traffic.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (72km — 8% of network): broadly stable</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">5%</div>
                <div className="text-sm text-rose-800">RED (2024)</div>
                <div className="text-xs text-surface-500">up from 2% in 2020</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">17%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">down from 20% in 2021</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">78%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">flat on 2021</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              B and C roads look steadier than A-roads on published data, but 22% still sits in amber or red —
              maintenance soon or now, on the council's own definitions.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">GREEN Spend, AMBER Condition</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£3.18m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2024/25</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£5.18m</div>
                <div className="text-brand-300 text-sm">Actual capital spend 2024/25</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">88%</div>
                <div className="text-brand-300 text-sm">Preventative maintenance share 2024/25</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              North Tyneside spent <span className="text-emerald-400">£6.49m on highway maintenance in 2024/25</span> —
              including £2m of council capital on top of DfT funding — and plans 90% preventative spend in 2025/26.
              The DfT Spend scorecard is GREEN. The Condition scorecard is still AMBER, because published road
              condition has not kept pace with the investment story.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road majority */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 722km Estate-Road Majority
          </h2>
          <p className="text-surface-600 mb-8">
            80% of North Tyneside's carriageway network is unclassified — where most pothole claims happen
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Approximate RED length</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">4%</td>
                  <td className="px-6 py-4 text-center text-surface-600">~29km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center">5%</td>
                  <td className="px-6 py-4 text-center text-surface-600">~36km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center">6%</td>
                  <td className="px-6 py-4 text-center text-surface-600">~43km</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">11%</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">~79km</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">10%</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">~72km</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Where Claims Actually Happen</h3>
              <p className="text-surface-600 text-sm mb-3">
                North Tyneside maintains 898km of roads: 104km of A-roads, 72km of B and C roads, and{' '}
                <strong>722km of unclassified roads</strong>. It also maintains 1,700km of footways, 147km of
                public rights of way and 89km of cycleways.
              </p>
              <p className="text-surface-600 text-sm">
                Most residential pothole damage occurs on U-roads. At 10% RED on 722km, roughly{' '}
                <strong>72km of estate highway</strong> is in condition the council categorises as "should be
                considered for maintenance."
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Annual AEI Coverage</h3>
              <p className="text-amber-800 text-sm mb-3">
                Since 2023, North Tyneside has assessed its full carriageway network annually using Annual
                Engineering Inspections (AEI) — visual surveys by qualified highway engineers, analysed via
                the Expert Asset platform.
              </p>
              <p className="text-amber-800 text-sm">
                That is stronger coverage than councils surveying U-roads only every two or four years. But
                annual network surveys still do not prove the specific pothole that damaged your car was
                identified and repaired in time — especially where AEI flags a section as amber while reactive
                works lag behind.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under Section 58 of the Highways Act 1980, North Tyneside must show it took reasonable care to
              secure the relevant highway was not dangerous. For estate roads, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Did AEI classify your road section as red or amber before your incident?</li>
              <li>• If 10% of U-roads are RED borough-wide, what treatment did your street receive?</li>
              <li>• Did a twice-yearly safety inspection pass the defect before it met intervention criteria?</li>
              <li>• Were there prior reports through the council's online roads form or FixMyStreet?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot point to borough-wide AEI data while leaving a reported defect unrepaired on a
              road it already classified as needing maintenance.
            </p>
          </div>
        </div>
      </section>

      {/* The Repair Conveyor Belt */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            34,739 Estimated Pothole Repairs in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair volume from North Tyneside's own transparency report — with an important counting caveat
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Estimated potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Carriageway resurfaced (km)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Carriageway preserved (km)</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">7,612</td>
                  <td className="px-6 py-4 text-center">7.7</td>
                  <td className="px-6 py-4 text-center">5.3</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">7,716</td>
                  <td className="px-6 py-4 text-center">10.88</td>
                  <td className="px-6 py-4 text-center">6.84</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">6,832</td>
                  <td className="px-6 py-4 text-center">7.79</td>
                  <td className="px-6 py-4 text-center">6.0</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">6,534</td>
                  <td className="px-6 py-4 text-center">10.6</td>
                  <td className="px-6 py-4 text-center">7.8</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold">6,045</td>
                  <td className="px-6 py-4 text-center">8.32</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">2.33</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total / average</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">34,739 / ~6,948 yr</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">—</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The Counting Caveat</h3>
              <QuoteCard
                quote="It should be noted that our works ordering system does not differentiate between pothole repairs and other types of reactive bituminous carriageway repairs. As such it is not possible to generate a report showing pothole repairs only. The above figures are therefore estimates based on our best assessment of the works ordering data."
                source="North Tyneside Council Highway Maintenance Transparency Report (October 2025)"
              />
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Preservation Fell Sharply in 2024/25</h3>
              <p className="text-amber-800 text-sm mb-3">
                North Tyneside treated 11km of carriageway with preventative maintenance in 2024/25, but
                preservation works — surface dressing, micro-surfacing and rejuvenation — dropped from 7.8km
                in 2023/24 to just <strong>2.33km</strong> in 2024/25.
              </p>
              <p className="text-amber-800 text-sm">
                The council says pothole fills have "gradually reduc[ed]" and attributes that to preventative
                emphasis. Your claim should still focus on the defect that damaged your vehicle, not the
                borough-wide trend line.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The AEI Admission */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The AEI Survey Switch — In The Council's Own Words
          </h2>
          <p className="text-surface-600 mb-8">
            North Tyneside moved to Annual Engineering Inspections in 2023 and documents what that did to the numbers
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="In 2023, North Tyneside made the proactive decision to move to Annual Engineering Inspections (AEI) for the classified and unclassified road network in line with DfT widening the scope for local authorities to explore different technologies."
              source="North Tyneside Council Highway Maintenance Transparency Report (October 2025)"
            />
            <QuoteCard
              quote="The change to AEI resulted in a significant jump in the red rated roads figures for all categories of road. But whilst AEI may indicate a higher proportion of the network in poor condition compared to SCANNER, this reflects its holistic, engineering-led evaluation of entire maintenance lengths."
              source="North Tyneside Council Highway Maintenance Transparency Report (October 2025)"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What AEI Does Differently</h3>
              <p className="text-surface-600 text-sm mb-3">
                Unlike SCANNER laser surveys measuring 10-metre sub-sections, AEI evaluates entire road sections
                and recommends treatment types — resurfacing, preservation or revenue patching — aligned with how
                maintenance schemes are actually delivered.
              </p>
              <p className="text-surface-600 text-sm">
                North Tyneside says AEI aligns with the PAS 2161 standard coming into effect from 2026/27. That
                forward planning supports its AMBER best-practice scorecard, even while condition remains AMBER.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Questions Worth Asking</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• Was your road surveyed under SCANNER or AEI when the defect formed?</li>
                <li>• If AEI flagged your section red or amber, was the recommended treatment delivered?</li>
                <li>• Does the council cite post-2023 data to defend a pre-2023 incident?</li>
                <li>• Did insurance trends or member feedback prioritise your road — or defer it?</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Inspection regime */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Inspections, Symology and Section 58
          </h2>
          <p className="text-surface-600 mb-8">
            How North Tyneside says it finds and schedules repairs — and where claims actually turn
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Dual Systems</h3>
              <p className="text-surface-600 text-sm mb-3">
                North Tyneside runs annual AEI condition surveys for strategic programming and{' '}
                <strong>safety inspections at least twice yearly</strong>, logged in the Symology system
                alongside responsive works scheduling.
              </p>
              <p className="text-surface-600 text-sm">
                Maintenance is prioritised using condition data, inspector intelligence, insurance trends and
                elected member input. LANTRA-accredited inspectors deliver the risk-based inspection regime
                aligned with Well-Managed Highway Infrastructure guidance.
              </p>
            </div>
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Performance Monitoring</h3>
              <p className="text-surface-600 text-sm mb-3">
                The council reviews reactive maintenance performance monthly through Symology data and says
                additional resources are allocated when inspection targets are at risk.
              </p>
              <p className="text-surface-600 text-sm">
                Its Highway Asset Management Plan runs 2017–2032, with a Highway Infrastructure Resilient
                Network Strategy for extreme weather. Expect a documented Section 58 defence — your evidence
                must beat the policy on the specific defect.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend, annual AEI surveys and a published HAMP, your claim lives or dies on
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council online form) — proof of actual notice</li>
              <li>• Photos showing defect size, depth and visible age (weathered edges, previous patching)</li>
              <li>• Road class — on a U-road in the 72km RED backlog, cite AEI classification for your section</li>
              <li>• Whether preservation works reached your road before pothole patching became necessary</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites North Tyneside's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* What AMBER + GREEN spend means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Well-Funded AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: North Tyneside invests seriously — here's how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — capital spend 63% above DfT allocation in 2024/25
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 88–90% preventative maintenance share across recent years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Full-network annual AEI surveys on classified and unclassified roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented HAMP, Symology inspection logging and LANTRA-accredited inspectors
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> B/C road condition broadly stable at 78% green in 2024
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured Section 58 defence on well-trafficked classified roads. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — A-road RED up from 2% to 14% on published data
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> U-road RED more than doubled from 4% to 11% before easing to 10%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits AEI switch caused a "significant jump" in red-rated roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> ~34,739 estimated pothole repairs in five years — defects still form reactively
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Carriageway preservation fell from 7.8km to 2.33km in 2024/25
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Pothole counts are estimates — the council cannot isolate pothole-only repairs
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT allocation</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Preventative %</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£3.32m</td>
                  <td className="px-4 py-3 text-center">£5.32m</td>
                  <td className="px-4 py-3 text-center">£1.26m</td>
                  <td className="px-4 py-3 text-center">89%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£3.18m</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-700">£5.18m</td>
                  <td className="px-4 py-3 text-center">£1.32m</td>
                  <td className="px-4 py-3 text-center">88%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono">2025/26 (projected)</td>
                  <td className="px-4 py-3 text-center">£4.53m</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-700">£6.53m</td>
                  <td className="px-4 py-3 text-center">£1.32m</td>
                  <td className="px-4 py-3 text-center">90%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-16 bg-white border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-4">
            Report the pothole to North Tyneside first
          </h2>
          <p className="text-surface-600 mb-6">
            North Tyneside's transparency report covers reactive maintenance including pothole repairs, urgent
            carriageway response and safety-related works. Reporting the defect through the council creates a
            dated record — useful evidence if the pothole was reported before your incident, or if the council
            failed to repair it within a reasonable time.
          </p>
          <a
            href="https://www.northtyneside.gov.uk/roads-pavements-and-transport/report-problems-roads-and-pavements"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-8 py-4 bg-brand-900 text-white font-semibold rounded-xl hover:bg-brand-800 transition-colors"
          >
            Report a pothole to North Tyneside Council
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-surface-500 text-sm mt-4">
            Use the council's online form to report potholes, flooding, pavements and signs. Keep your reference number and any confirmation emails.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in North Tyneside?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No AEI methodology-change argument</li>
                <li>• No prior-report search</li>
                <li>• No U-road RED backlog cited</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ A-road RED rise from 2% to 14% documented</li>
                <li>✅ 722km U-road majority and RED backlog argued</li>
                <li>✅ 34,739 estimated repairs in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to North Tyneside</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=north-tyneside"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My North Tyneside Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                North Tyneside has a GREEN spend scorecard — can I still claim for pothole damage?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN, but your claim turns on whether the specific defect that
                damaged your vehicle was reasonably inspected and repaired under Section 58 — not on aggregate spend.
                North Tyneside is AMBER overall for condition, its A-road RED share rose from 2% to 14% on published
                data, and U-road RED more than doubled from 4% to 11% before easing to 10%. GREEN spend does not erase
                those condition trends.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What does the jump in A-road RED condition mean for my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                North Tyneside's own transparency report shows A-road RED condition at 2% in 2020 and 2021, then 13%
                in 2023 and 14% in 2024. The council attributes much of the jump to switching from SCANNER to Annual
                Engineering Inspections in 2023. If the council cites improving methodology while your incident predates
                or sits inside the transition, ask whether pre- and post-2023 condition records are comparable for your
                specific road.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 722km — 80% of North Tyneside's 898km carriageway network. The council's published RED
                share for U-roads rose from 4% in 2020 to 11% in 2023, then 10% in 2024 — roughly 72km of estate highway
                in RED condition at the latest survey. North Tyneside now assesses the full carriageway network annually
                using AEI, but pothole claims still turn on whether your specific defect was identified, prioritised and
                repaired in time.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Are the pothole repair figures reliable?
              </h3>
              <p className="text-surface-600 text-sm">
                Treat them as estimates. North Tyneside's own report states its works ordering system "does not
                differentiate between pothole repairs and other types of reactive bituminous carriageway repairs", so
                published pothole counts are the council's best assessment of works-ordering data, not a precise
                pothole-only tally. The trend still matters: an estimated 34,739 repairs over five years, averaging
                roughly 6,948 per year, shows a network that routinely produces reactive defects.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the AEI survey switch weaken a Section 58 defence?
              </h3>
              <p className="text-surface-600 text-sm">
                It can cut both ways. North Tyneside says AEI evaluates entire road sections rather than 10-metre SCANNER
                sub-sections and admits the change "resulted in a significant jump in the red rated roads figures for all
                categories of road." If the council relies on network-level knowledge under Section 58, you can ask whether
                the survey that flagged your road was SCANNER or AEI, and whether treatment recommendations from AEI were
                acted on before your incident.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                North Tyneside inspects roads twice a year — is that enough?
              </h3>
              <p className="text-surface-600 text-sm">
                Safety inspections at least twice yearly are logged in the Symology system, separate from annual AEI
                condition surveys. Section 58 is defect-specific: the question is whether your pothole should have been found
                on a safety inspection, reported by a resident, or flagged by AEI before it damaged your vehicle. Prior
                reports through the council's online roads form or FixMyStreet — combined with photos showing defect age —
                are often stronger than arguing inspection frequency alone.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why is Best Practice AMBER when the council reports 88–90% preventative spend?
              </h3>
              <p className="text-surface-600 text-sm">
                The DfT Best Practice scorecard measures more than spend split — treatment intensity, innovation adoption
                and asset-management maturity all feed in. North Tyneside publishes strong preventative percentages and trials
                warm-mix and rubberised materials, yet carriageway preservation fell from 7.8km in 2023/24 to 2.33km in
                2024/25 while A-road RED condition climbed on published data. AMBER best practice alongside AMBER condition
                means expect a prepared Section 58 defence, but one that still has to answer for your specific defect.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.northtyneside.gov.uk/roads-pavements-and-transport/highway-maintenance-transparency-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              North Tyneside Council Highway Maintenance Transparency Report (October 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
